/**
 * The aerodrome VIEW AIRPLANES parks its aeroplane on — the original's own
 * scene, not a set built to look like one.
 *
 * ## What the original draws, and where it comes from
 *
 * The shell's plane viewer is a live 3D render (`docs/game/ui-reference/index.md`,
 * "View Airplanes → 3D viewer"), and the scene behind the aeroplane is
 * `PTPLANE.TBL` / `PTPLANE.WLD` — the practice range, a **shell-side** asset:
 * gi-format.md's "`PTPLANE.GI` is never loaded by `RB.EXE` … the string
 * `ptplane` appears in **`PS.EXE`**" is what ties that library to this screen,
 * and missions.md's own VIEW AIRPLANES entry names `ptplane.tbl` outright.
 * Both are already baked, so this module *places* the original's objects; it
 * invents no scenery, and every position below is a record in
 * `data/baked/world/PTPLANE.json`.
 *
 * The proof that it is this scene and not a look-alike is the palette.
 * shading-and-paint.md measured a 640x400 capture of the shell's 3D viewer at
 * **79 distinct colours, every one an exact `BARON.PAL` entry** — and each of
 * them is an object in this region:
 *
 * | what is on screen | index | where it comes from |
 * |---|---|---|
 * | sky, zenith to horizon | 48 → 63 | the 16-band run `time-of-day.ts` recovered |
 * | ground at the horizon | 119 `#7d7149` | the flat base plane, `AERODROME_BASE_GROUND` |
 * | the big field | 44 `#b2794d` | `PTPLANE-09`'s 348 x 234 m plate |
 * | the apron | 127 `#927559` | `PTPLANE-09`'s 113 x 210 m marking |
 * | **the ground under the aeroplane** | **125 `#795538`** | **`PTPLANE-09`'s 42 x 130 m runway strip** |
 * | the shed on the horizon | greys + 132 roof | `PTPLANE-05`, 44.9 x 17.9 m |
 * | the flag on its pole | 64 / 84 / 30 | `PTPLANE-08`, 11.5 m tall |
 * | the tree line to one side | 133 / 134 canopies | `PTPLANE-12`, the copse |
 * | the aeroplane's own shadow | 64 `#141414` | `PTPLANE-13`…`31`; see `parked-aircraft.ts` |
 *
 * ## The sky is banded here, and that is not a contradiction
 *
 * `flight/world.ts` records the *flight* sim's sky as one flat `#7db6c3` with a
 * hard horizon, measured under DOSBox-X. This screen's own capture is
 * unambiguously the **16-step 48→63 run**: read down either edge it steps
 * `#7db6c3`, `#82b6be`, `#86b6be` … to `#b6b6b6` at the skyline, which is
 * `BARON.PAL` 48 through 63 in order. So the shipped game does draw the banded
 * run `time-of-day.ts` recovered from `sun.c`'s `setSkyShades(48, 63)`, and
 * this screen is where it can be seen. That is why the run is taken from
 * `skyLookFor` unconditionally rather than riding a treatment: here the
 * bands are the original's own picture, not a port extension, so there is
 * nothing to switch off.
 *
 * ## Depth
 *
 * Everything is opaque with `depthWrite`/`depthTest` untouched, under the
 * reversed-Z buffer `parked-preview.ts` sets up — because this region's hills
 * stand 10 to 18 km out while the aeroplane is 12 m away. lessons.md records
 * three separate outages caused by reaching for `depthWrite: false` around a
 * problem of this shape; nothing here opts a layer out.
 */

import * as THREE from "three/webgpu";
import { loadWorldRegion } from "../world/region-loader.ts";
import { buildWorldInstances, type WorldInstances } from "../three/world-instancer.ts";
import { createGround, createSky, GROUND_HALF_EXTENT_M } from "../flight/world.ts";
import { skyLookFor } from "../flight/time-of-day.ts";
import { disposeTree } from "../three/dispose-tree.ts";
import { AERODROME_BASE_GROUND, PTPLANE_REGION_PATH, RUNWAY_ORIGIN } from "./parked-look.ts";
import { enhancedIndexOnce } from "../three/enhanced-catalog.ts";

export interface AerodromeStage {
  /** Sky, base ground and the region's scenery — add to the scene once. */
  readonly group: THREE.Group;
  /** Draw calls the scenery costs, for a capture script that wants to count them. */
  readonly drawCallCount: number;
  /** Placement refs whose model would not load — skipped, never fatal, the same precedent `/fly`'s region loader sets. */
  readonly failedRefs: readonly string[];
  /** ENHANCED MODELS for the field's own huts, live with the toggle row (`three/enhanced-structure.ts`). */
  readonly setEnhancedStructures: (enabled: boolean) => void;
  dispose(): void;
}

/**
 * Builds the stage. Rejects only if the region JSON itself cannot be fetched;
 * an individual object that fails to load is skipped by the instancer and
 * reported in `failedRefs`, because a missing shed should cost the shed and not
 * the screen.
 */
export async function createAerodromeStage(enhanced = true): Promise<AerodromeStage> {
  const group = new THREE.Group();
  group.add(createSky(skyLookFor("daytime", true).sky));
  group.add(createGround(GROUND_HALF_EXTENT_M, AERODROME_BASE_GROUND));

  const region = await loadWorldRegion(PTPLANE_REGION_PATH);
  // ENHANCED MODELS reaches the field's own buildings, which is the one place in
  // the port that draws a *modelled* aerodrome: `PTPLANE-06` is the hut row down
  // the eastern boundary, four copies at 2 048 `.WLD` units apart, and the
  // scenery roster covers it (`tools/enhanced/src/scenery-roster.ts` — by box
  // congruence with Dunkirk's, because `PTPLANE.GI` is the one collision table
  // `RB.EXE` never loads).
  const enhancedModels = await enhancedIndexOnce().catch(() => undefined);
  const instances: WorldInstances = await buildWorldInstances(
    region.placements,
    RUNWAY_ORIGIN,
    enhancedModels && enhancedModels.size > 0 ? { enhancedModels } : {},
  );
  instances.enhancedStructures.set(enhanced);
  group.add(instances.group);

  return {
    group,
    drawCallCount: instances.drawCallCount,
    failedRefs: instances.failedRefs,
    setEnhancedStructures: instances.enhancedStructures.set,
    dispose() {
      disposeTree(group);
    },
  };
}
